import { createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import { PaginationType } from '../../types/utils/pagination.type';
import { RouteParamsType } from '../../types/utils/router-params.type';
import { additionalQueryParams } from '../../utils/utilities';
import { setModalContent, setShowModal } from '../utils/utils.slice';
import { TicketType } from '../../types/ticket/ticket.type';
import { TicketListItemType } from '../../types/ticket/TicketListItem.type';
import { UserType } from '../../types/user/user.type';
import { TICKET_TYPE } from '../../utils/constants';
import { getOrderDetails } from '../orders/orders.action';
import { getPortingDetails } from '../porting/porting.action';
import { TICKET_STATUS } from '../../types/enums/ticket-status.enum';
import { TICKET_PRIORITY } from '../../types/enums/ticket-priority.type';
import { setTicketDetails } from './tickets.slice';
import { TicketCommentType } from '../../types/ticket/ticket-comment/ticket-comment.type';
import { ORG_LEVEL } from '../../types/enums/organization-level.enum';
import { TicketAttachmentType } from '../../types/ticket/ticket-attachment/ticket-attachment.type';
import { rejectWithApiError } from '../../utils/api';
import { RootReducerState } from '../../redux/Reducers';
import { isCustomerTicketCollaborator } from '../../views/Main/Tickets/Tickets.permissions';
import { TicketDepartment } from '../../types/enums/ticket-department.enum';
import { TICKET_ACTION } from '../../types/enums/ticket-action.type';
import { CcNotifyUser } from '../../types/ticket/cc-notify-user.type';
import { SupportTicketsStats } from '../../types/ticket/support-tickets-stats.type';

export const postTicketDetails = createAsyncThunk<
  TicketType,
  {
    data: {
      name: string;
      description: string;
      organization: { id: number };
      location: { id: number; displayName: string } | null;
      service: { id: number; displayName: string } | null;
      status: TICKET_STATUS;
      group?: { id: number } | null;
      technician?: Pick<UserType, 'id' | 'email' | 'lastname' | 'firstname'> | null;
      mobile: { number: string; countryCode: string } | null;
      priority: TICKET_PRIORITY;
      assignee: Pick<UserType, 'id' | 'email' | 'lastname' | 'firstname'>;
      department: TicketDepartment;
      techName: string | null;
      techEmail: string | null;
      techMobile: { number: string; countryCode: string } | null;
    };
    files: Array<File>;
  },
  { state: RootReducerState }
>('tickets/postTicketDetails', async ({ data, files }, thunkAPI) => {
  try {
    const formData = new FormData();
    formData.append('body', JSON.stringify(data));
    if (files && files.length > 0) {
      files.forEach(file => {
        formData.append('files', file);
      });
    }

    const response = await axios.post<TicketType>(`${process.env.REACT_APP_API_URL}/ticket`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error during creating ticket!' });
  }
});

export const getTicketsList = createAsyncThunk<
  PaginationType<TicketListItemType>,
  RouteParamsType & { controller?: AbortController },
  { state: RootReducerState }
>('tickets/getTicketsList/no-loader', async (data, thunkAPI) => {
  const { controller, ...params } = data;
  try {
    const additionalParams = additionalQueryParams(
      thunkAPI.getState().sharedReducer.selectedCompany,
      thunkAPI.getState().sharedReducer.selectedLocation,
      thunkAPI.getState().sharedReducer.includeSuborgs,
      thunkAPI.getState().sharedReducer.selectedCountry
    );
    const mergedParams = { ...params, ...additionalParams };
    const queryString = Object.keys(mergedParams)
      .map(key => key + '=' + mergedParams[key])
      .join('&');

    const response = await axios.get<PaginationType<TicketListItemType>>(
      `${process.env.REACT_APP_API_URL}/ticket${queryString ? `?${queryString}` : ''}`,
      controller ? { signal: controller.signal } : undefined
    );
    return response.data;
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error during fetching tickets!' });
  }
});

export const getTicketDetails = createAsyncThunk<TicketType, { id: number }, { state: RootReducerState }>(
  'tickets/getTicketDetails',
  async ({ id }, thunkAPI) => {
    try {
      const response = await axios.get<TicketType>(`${process.env.REACT_APP_API_URL}/ticket/${id}`);
      return response.data;
    } catch (error) {
      return rejectWithApiError({ thunkAPI, error, fallback: 'Error during fetching ticket!' });
    }
  }
);

/** @deprecated TC-3071 - still uses the old ticketing system */
export const patchTicketDetails = createAsyncThunk<
  TicketType,
  { id: number; data: unknown },
  { state: RootReducerState }
>('tickets/patchTicketDetails', async ({ data, id }, thunkAPI) => {
  try {
    const response = await axios.patch<TicketType>(`${process.env.REACT_APP_API_URL}/tickets/${id}`, data);
    return response.data;
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error during updating ticket!' });
  }
});

export const patchNewTicketDetails = createAsyncThunk<
  TicketType,
  {
    id: number;
    data: {
      name?: string;
      organization?: { id: number };
      location?: { id: number; displayName: string } | null;
      service?: { id: number; displayName: string } | null;
      mobile?: { number: string; countryCode: string } | null;
      group?: { id: number } | null;
      technician?: Pick<UserType, 'id' | 'email' | 'lastname' | 'firstname'> | null;
      priority?: TICKET_PRIORITY;
      assignee?: Pick<UserType, 'id' | 'email' | 'lastname' | 'firstname'>;
      department?: TicketDepartment;
      ccNotifyUsers?: CcNotifyUser[];
      status?: TICKET_STATUS;
      techName?: string | null;
      techEmail?: string | null;
      techMobile?: { number: string; countryCode: string } | null;
    };
  },
  { state: RootReducerState }
>('tickets/patchNewTicketDetails', async ({ data, id }, thunkAPI) => {
  try {
    const response = await axios.patch<TicketType>(`${process.env.REACT_APP_API_URL}/ticket/${id}`, data);
    thunkAPI.dispatch(getTicketDetails({ id }));
    return response.data;
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error during updating ticket!' });
  }
});

export const updateTicketAction = createAsyncThunk<
  void,
  { id: number; action: TICKET_ACTION },
  { state: RootReducerState; rejectValue: string }
>('tickets/updateTicketAction', async ({ id, action }, thunkAPI) => {
  try {
    await axios.patch(`${process.env.REACT_APP_API_URL}/ticket/${id}`, { action });
    thunkAPI.dispatch(getTicketDetails({ id }));
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error });
  }
});

export const getTicketUsersList = createAsyncThunk<
  { items: Array<UserType> },
  void,
  { state: RootReducerState; rejectValue: string }
>('tickets/getTicketUsersList', async (_, thunkAPI) => {
  try {
    const userRole = thunkAPI.getState().authReducer.user?.role;
    if (isCustomerTicketCollaborator(userRole)) {
      return { items: [] };
    }

    const response = await axios.get<{ items: UserType[] }>(`${process.env.REACT_APP_API_URL}/users/ticket`);
    return response.data;
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error during fetching ticket users list!' });
  }
});

export const postTicketComment = createAsyncThunk<
  TicketCommentType,
  {
    data: {
      message: string;
      isInternal: boolean;
      ticket: { id: number };
      orgLevel: ORG_LEVEL;
      mentionedUsers?: Array<{ id: number }>;
      markAsWaitingForResponse?: boolean;
    };
  },
  {
    state: RootReducerState;
  }
>('comment/postTicketComment', async ({ data }, thunkAPI) => {
  try {
    const response = await axios.post<TicketCommentType>(`${process.env.REACT_APP_API_URL}/ticket/comment`, data);
    return response.data;
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error during creating comment!' });
  }
});

/** @deprecated TC-3071 - still uses the old ticketing system */
export const postCommentDetails = createAsyncThunk<
  TicketType,
  { data: unknown; type?: string },
  { state: RootReducerState }
>('comments/postCommentDetails', async ({ data, type }, thunkAPI) => {
  try {
    const response = await axios.post<TicketType>(`${process.env.REACT_APP_API_URL}/comments`, data);

    if (type === TICKET_TYPE.ORDER) {
      const id = thunkAPI.getState().ordersReducer.orderDetails?.id;
      if (id) thunkAPI.dispatch(getOrderDetails({ id }));
    } else if (type === TICKET_TYPE.PORT_REQUEST) {
      const id = thunkAPI.getState().portingsReducer.portingDetails?.id;
      if (id) thunkAPI.dispatch(getPortingDetails({ id }));
    } else {
      const id = thunkAPI.getState().ticketsReducer.ticketDetails?.id;
      if (id) thunkAPI.dispatch(getTicketDetails({ id }));
    }

    return response.data;
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error during creating comment!' });
  }
});

/** @deprecated TC-3071 - still uses the old ticketing system */
export const postAttachmentDetails = createAsyncThunk<
  TicketType,
  { payload: { ticket: { id: number } }; data: unknown },
  { state: RootReducerState }
>('comments/postAttachmentDetails', async ({ data, payload }, thunkAPI) => {
  try {
    const response = await axios.post<TicketType>(`${process.env.REACT_APP_API_URL}/attachments`, data, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });

    if (payload.ticket) {
      thunkAPI.dispatch(getTicketDetails({ id: payload.ticket.id }));
    }

    thunkAPI.dispatch(setModalContent(null));
    thunkAPI.dispatch(setShowModal(false));

    return response.data;
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error during creating attachment!' });
  }
});

/** @deprecated TC-3071 - still uses the old ticketing system */
export const deleteAttachmentDetails = createAsyncThunk<TicketType, { id: number }, { state: RootReducerState }>(
  'tickets/deleteAttachmentDetails',
  async ({ id }, thunkAPI) => {
    try {
      const response = await axios.delete<TicketType>(`${process.env.REACT_APP_API_URL}/attachments/${id}`);
      thunkAPI.dispatch(setShowModal(false));
      thunkAPI.dispatch(setModalContent(null));
      return response.data;
    } catch (error) {
      return rejectWithApiError({ thunkAPI, error, fallback: 'Error during deleting attachment!' });
    }
  }
);

const getFileTypeFromMimeType = (mimeType: string): string => {
  if (mimeType.startsWith('image/')) return 'image';
  if (mimeType.startsWith('video/')) return 'video';
  if (mimeType.startsWith('audio/')) return 'audio';
  if (
    mimeType === 'application/pdf' ||
    mimeType.includes('document') ||
    mimeType.includes('msword') ||
    mimeType.includes('spreadsheet') ||
    mimeType.includes('presentation')
  )
    return 'document';
  return 'other';
};

export const postTicketAttachmentDetails = createAsyncThunk<
  TicketAttachmentType,
  {
    data: {
      file: File;
      ticket: { id: number };
      title?: string;
      description?: string;
      fileType?: string;
    };
  },
  { state: RootReducerState }
>('comments/postTicketAttachmentDetails', async ({ data }, thunkAPI) => {
  try {
    const formData = new FormData();
    formData.append('file', data.file);
    formData.append('body', JSON.stringify({ ticket: data.ticket }));
    if (data.title) formData.append('title', data.title);
    if (data.description) formData.append('description', data.description);
    formData.append('fileType', data.fileType || getFileTypeFromMimeType(data.file.type));

    const response = await axios.post<TicketAttachmentType>(
      `${process.env.REACT_APP_API_URL}/ticket/attachment`,
      formData,
      {
        headers: { 'Content-Type': 'multipart/form-data' },
      }
    );

    const { ticketDetails } = thunkAPI.getState().ticketsReducer;
    if (ticketDetails) {
      thunkAPI.dispatch(getTicketDetails({ id: ticketDetails.id }));
    }

    return response.data;
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error during creating attachment!' });
  }
});

export const postTicketAttachmentsDetails = createAsyncThunk<
  TicketAttachmentType[],
  {
    data: {
      files: Array<{ file: File; title?: string; description?: string; fileType?: string }>;
      ticket: { id: number };
    };
  },
  { state: RootReducerState }
>('comments/postTicketAttachmentsDetails', async ({ data }, thunkAPI) => {
  try {
    const formData = new FormData();
    data.files.forEach(fileData => {
      formData.append('files', fileData.file);
    });

    const filesMetadata = data.files.map(fileData => ({
      title: fileData.title || fileData.file.name,
      description: fileData.description || '',
      fileType: fileData.fileType || getFileTypeFromMimeType(fileData.file.type),
    }));

    formData.append('body', JSON.stringify({ ticket: data.ticket, files: filesMetadata }));

    const response = await axios.post<TicketAttachmentType[]>(
      `${process.env.REACT_APP_API_URL}/ticket/attachments`,
      formData,
      {
        headers: { 'Content-Type': 'multipart/form-data' },
      }
    );

    const { ticketDetails } = thunkAPI.getState().ticketsReducer;
    if (ticketDetails) {
      thunkAPI.dispatch(getTicketDetails({ id: ticketDetails.id }));
    }

    return response.data;
  } catch (error: unknown) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error during creating attachments!' });
  }
});

export const deleteTicketAttachmentDetails = createAsyncThunk<TicketType, { id: number }, { state: RootReducerState }>(
  'tickets/deleteTicketAttachmentDetails',
  async ({ id }, thunkAPI) => {
    try {
      const response = await axios.delete<TicketType>(`${process.env.REACT_APP_API_URL}/ticket/attachment/${id}`);
      thunkAPI.dispatch(setShowModal(false));
      thunkAPI.dispatch(setModalContent(null));
      const { ticketDetails } = thunkAPI.getState().ticketsReducer;
      if (ticketDetails) {
        thunkAPI.dispatch(
          setTicketDetails({
            ...ticketDetails,
            attachments: (ticketDetails.attachments || []).filter(a => a.id !== id),
          })
        );
      }

      return response.data;
    } catch (error) {
      return rejectWithApiError({ thunkAPI, error, fallback: 'Error during deleting attachment!' });
    }
  }
);

export const getTicketGroups = createAsyncThunk<
  { items: Array<{ id: number; name: string; members: Array<UserType> }> },
  void,
  { state: RootReducerState }
>('tickets/getTicketGroups', async (_, thunkAPI) => {
  try {
    const response = await axios.get<{ items: Array<{ id: number; name: string; members: Array<UserType> }> }>(
      `${process.env.REACT_APP_API_URL}/ticket/group`
    );
    return response.data;
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error during fetching ticket groups!' });
  }
});

export const downloadTicketAttachment = createAsyncThunk<
  string,
  {
    id: number;
  },
  { state: RootReducerState }
>('tickets/downloadTicketAttachment', async ({ id }, thunkAPI) => {
  try {
    const response = await axios.get<{
      downloadUrl: string;
    }>(`${process.env.REACT_APP_API_URL}/ticket/attachment/${id}`);

    window.open(response.data.downloadUrl, '_blank');

    return response.data.downloadUrl;
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error downloading attachment!' });
  }
});

export const getSupportTicketsStats = createAsyncThunk<
  SupportTicketsStats,
  { controller?: AbortController },
  { state: RootReducerState; rejectValue: string }
>('tickets/getSupportTicketsStats/no-loader', async ({ controller }, thunkAPI) => {
  try {
    const routeParams = thunkAPI.getState().ticketsReducer.ticketsRouteParams;

    const additionalParams = additionalQueryParams(
      thunkAPI.getState().sharedReducer.selectedCompany,
      thunkAPI.getState().sharedReducer.selectedLocation,
      thunkAPI.getState().sharedReducer.includeSuborgs,
      thunkAPI.getState().sharedReducer.selectedCountry
    );

    const response = await axios.get<SupportTicketsStats>(`${process.env.REACT_APP_API_URL}/ticket/stats`, {
      params: { ...routeParams, ...additionalParams },
      signal: controller?.signal,
    });

    return response.data;
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error });
  }
});
